import { orderBy } from "lodash";
import { AncestryPoint } from "server/coreApi/accessPoints/get/models.js";
import {
  IdentityIsoResult,
  PointResult,
  SearchResponse,
} from "server/coreApi/search/get/models.js";

import {
  ACCESS_PARENT_KEY,
  CONTAINER_KEY,
  CUSTOMER_REFERENCE_KEY,
  DEPOT_KEY,
  IDENTITY_KEY,
  NormalizedSearchResult,
  SearchDataType,
  TERMINAL_KEY,
} from "./types";

export function normalizeSearchData(
  data: SearchResponse["indices"],
): NormalizedSearchResult[] {
  return orderBy(
    [
      ...(data.pointName?.values ?? []).map((value) => ({
        type: SearchDataType.POINT_NAME,
        ...value,
      })),
      ...(data.customerCRMKey?.values ?? []).map((value) => ({
        type: SearchDataType.CUSTOMER_CRM_KEY,
        ...value,
      })),
      ...(data.customerName?.values ?? []).map((value) => ({
        type: SearchDataType.CUSTOMER_NAME,
        ...value,
      })),
      ...(data.customerGeoLocationName?.values ?? []).map((value) => ({
        type: SearchDataType.CUSTOMER_GEOLOCATION,
        ...value,
      })),
      ...(data.identityIso?.values ?? [])
        .map((value) => ({
          type: SearchDataType.IDENTITY_ISO,
          ...value,
        }))
        // Filter out identity results that don't belong to a customer
        .filter((value: IdentityIsoResult) => !!value.ref),
    ],
    "score",
    "desc",
  );
}

export function labelKey(result: NormalizedSearchResult) {
  switch (result.type) {
    case SearchDataType.CUSTOMER_CRM_KEY:
      return CUSTOMER_REFERENCE_KEY;
    case SearchDataType.CUSTOMER_NAME:
      return CUSTOMER_REFERENCE_KEY;
    case SearchDataType.CUSTOMER_GEOLOCATION:
      return CUSTOMER_REFERENCE_KEY;
    case SearchDataType.POINT_NAME:
      switch ((result as PointResult).point.type) {
        case "TERMINAL":
          return TERMINAL_KEY;
        case "ACCESS_PARENT":
          return ACCESS_PARENT_KEY;
        case "GROUP":
          return DEPOT_KEY;
        case "ACCESS_POINT":
          return CONTAINER_KEY;
      }
      break;
    case SearchDataType.IDENTITY_ISO:
      return IDENTITY_KEY;
  }
}

export function urlToResult(
  result: NormalizedSearchResult,
  ancestry?: AncestryPoint,
) {
  switch (result.type) {
    case SearchDataType.CUSTOMER_CRM_KEY:
    case SearchDataType.CUSTOMER_NAME:
    case SearchDataType.CUSTOMER_GEOLOCATION:
      return `/customer/manage/${result.pk}`;
    case SearchDataType.POINT_NAME:
      if (!ancestry) {
        throw new Error("Ancestry is required for point results");
      }
      switch ((result as PointResult).point.type) {
        case "TERMINAL":
          return `infrastructure/manage/${result.pk}`;
        case "ACCESS_PARENT":
          return `infrastructure/manage/${ancestry.terminal.id}/${result.pk}`;
        case "GROUP":
          return `infrastructure/manage/${ancestry.terminal.id}/${ancestry.accessParent.id}/`; // Depot ids aren't represented in the URL
        case "ACCESS_POINT":
          return `infrastructure/manage/${ancestry.terminal.id}/${ancestry.accessParent.id}/${result.pk}`;
      }
      break;
    case SearchDataType.IDENTITY_ISO:
      const identityResult = result as IdentityIsoResult;
      return `/customer/manage/${identityResult.ref.customer.id}`;
  }
}
